import { useState, useEffect } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  Navigate,
  useParams,
} from 'react-router-dom';
import { useCommunities } from '../../hooks/use-communities';
import { useGetUserById } from '../../hooks/use-user';
import { useAuthStore } from '../../stores';
import { Navbar } from '../../components/layout/navbar';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Settings,
  ShieldAlert,
  ChevronLeft,
  Menu,
  X,
  Crown,
  Loader2,
  AlertTriangle,
  Activity,
  Ban,
  UserCog,
  Shield,
  Flag,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';

// Navigation items with role-based access
const getNavItems = (communitySlug) => [
  {
    title: 'Dashboard',
    href: `/forum/${communitySlug}/admin`,
    icon: LayoutDashboard,
    roles: ['owner', 'superAdmin', 'admin', 'moderator'],
  },
  {
    title: 'Members',
    href: `/forum/${communitySlug}/admin/members`,
    icon: Users,
    roles: ['owner', 'superAdmin', 'admin', 'moderator'],
  },
  {
    title: 'Discussions',
    href: `/forum/${communitySlug}/admin/discussions`,
    icon: MessageSquare,
    roles: ['owner', 'superAdmin', 'admin', 'moderator'],
  },
  {
    title: 'Reports',
    href: `/forum/${communitySlug}/admin/reports`,
    icon: Flag,
    roles: ['owner', 'superAdmin', 'admin', 'moderator'],
  },
  {
    title: 'Banned Members',
    href: `/forum/${communitySlug}/admin/banned`,
    icon: Ban,
    roles: ['owner', 'superAdmin', 'admin'],
  },
  {
    title: 'Settings',
    href: `/forum/${communitySlug}/admin/settings`,
    icon: Settings,
    roles: ['owner', 'superAdmin', 'admin'],
  },
  {
    title: 'Activity Log',
    href: `/forum/${communitySlug}/admin/activity`,
    icon: Activity,
    roles: ['owner', 'superAdmin', 'admin'],
  },
];

export default function CommunityAdminLayout() {
  const { communitySlug } = useParams();
  const { walletAddress } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Get user data
  const { data: user, isLoading: isLoadingUser } = useGetUserById(
    walletAddress
  );

  // Get community data
  const { useGetCommunityBySlug } = useCommunities();
  const {
    data: community,
    isLoading: isCommunityLoading,
    isError: isCommunityError,
  } = useGetCommunityBySlug(communitySlug);

  // Determine user's role in this community
  const [userRole, setUserRole] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (community && user) {
      const member = community.members?.find((m) => m.userOid === user.oid);
      const role = member?.role;

      if (role === 'owner' || role === 'admin' || role === 'moderator') {
        setHasAccess(true);
        setUserRole(role);
      } else {
        setHasAccess(false);
        setUserRole(null);
      }
    }
  }, [community, user]);

  // Get nav items filtered by role
  const navItems = getNavItems(communitySlug).filter(
    (item) => userRole && item.roles.includes(userRole)
  );

  // Get role badge styling
  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return {
          color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
          icon: Crown,
        };
      case 'admin':
        return {
          color: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
          icon: UserCog,
        };
      case 'moderator':
        return {
          color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
          icon: Shield,
        };
      default:
        return {
          color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
          icon: Users,
        };
    }
  };

  // Loading state
  if (isCommunityLoading || isLoadingUser) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex items-center gap-3 text-zinc-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Error state - community not found
  if (isCommunityError || !community) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Community Not Found</h2>
          <p className="text-zinc-400 mb-4">
            The community "{communitySlug}" could not be found.
          </p>
          <Link to="/forum" className="text-violet-400 hover:text-violet-300">
            Return to Communities
          </Link>
        </div>
      </div>
    );
  }

  // Access denied - not owner/admin/moderator
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <ShieldAlert className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-zinc-400 text-center mb-6 max-w-md">
            You don't have permission to access the admin panel for "
            {community.name}". Only community owners, admins, and moderators can
            access this area.
          </p>
          <Link
            to={`/forum/${communitySlug}`}
            className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300"
          >
            <ChevronLeft className="w-4 h-4" />
            Return to {community.name}
          </Link>
        </div>
      </div>
    );
  }

  const roleBadge = getRoleBadge(userRole);
  const RoleIcon = roleBadge.icon;

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="relative z-10 flex">
        {/* Sidebar */}
        <aside
          className={cn(
            'fixed lg:sticky top-0 lg:top-16 left-0 z-50 h-screen lg:h-[calc(100vh-64px)] w-64 bg-black/80 backdrop-blur-xl border-r border-white/10 transform transition-transform duration-300 ease-in-out lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              {community?.avatar ? (
                <img
                  src={community.avatar}
                  alt={community.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center">
                  <span className="text-lg font-bold">
                    {community?.name?.[0]}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h2 className="text-sm font-semibold truncate">
                  {community.name}
                </h2>
                <span
                  className={cn(
                    'text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1',
                    roleBadge.color
                  )}
                >
                  <RoleIcon className="h-3 w-3" />
                  {userRole}
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                      isActive
                        ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.title}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-white/10">
            <Link
              to={`/forum/${communitySlug}`}
              className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to community
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-h-[calc(100vh-64px)]">
          {/* Top Bar (Mobile) */}
          <header className="lg:hidden bg-black/50 backdrop-blur-xl border-b border-white/10 px-4 py-3 sticky top-16 z-30">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(true)}
                className="text-zinc-400 hover:text-white"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-sm font-semibold">Admin Panel</h1>
                <p className="text-xs text-zinc-500">{community.name}</p>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-6">
            <Outlet context={{ community, user, userRole }} />
          </main>
        </div>
      </div>
    </div>
  );
}
